import { create } from 'zustand'
import { useAuthStore } from '@/features/auth/store/auth.store'

/**
 * Auto-download preferences — which attachment kinds fetch their bytes
 * automatically, and which wait for an explicit tap.
 *
 * **Deliberately per-device and NOT synced.** This is a bandwidth policy, not
 * an account setting: a phone on cellular and a desktop on wifi should behave
 * differently, so it lives in `localStorage` rather than the SDK. Nothing here
 * touches the server.
 *
 * "Off" never hides the attachment — the bubble still shows filename, size and
 * a download control. It only prevents the automatic byte fetch.
 */

export type AutoDownloadKind = 'photos' | 'documents' | 'videos' | 'audio'

export interface AutoDownloadSettings {
  photos: boolean
  documents: boolean
  videos: boolean
  audio: boolean
}

/**
 * Everything off by default — the conservative choice: a new device downloads
 * nothing until the user opts in, rather than pulling every attachment in a
 * thread on first open.
 *
 * Only applies where nothing is stored yet. A user who has already set these
 * keeps their choices (see `load`).
 */
const DEFAULTS: AutoDownloadSettings = {
  photos: false,
  documents: false,
  videos: false,
  audio: false,
}

const STORAGE_PREFIX = 'instavc_auto_download'

/**
 * Cap on how many attachment URLs the unlocked set remembers. Without a cap a
 * long-lived account would grow this list forever; the most recent ones are
 * the ones still worth keeping around.
 */
const UNLOCKED_CAP = 2000

/**
 * Per-user-per-device.
 *
 * Per-DEVICE because this is a bandwidth policy — a phone on cellular should
 * not inherit what a desktop on wifi chose, so it never leaves the device.
 *
 * Per-USER because a shared browser or handset would otherwise hand the next
 * person who signs in the previous user's preferences.
 *
 * Signed out (no id yet) reads and writes an `anon` bucket, so toggles flipped
 * before the session resolves aren't lost — they simply don't follow the
 * account.
 */
function storageKey(userId: string | null): string {
  return `${STORAGE_PREFIX}:${userId || 'anon'}`
}

function load(userId: string | null): AutoDownloadSettings {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw) as Partial<AutoDownloadSettings>
    // Merge over defaults so a key added later doesn't come back undefined.
    return {
      photos: parsed.photos ?? DEFAULTS.photos,
      documents: parsed.documents ?? DEFAULTS.documents,
      videos: parsed.videos ?? DEFAULTS.videos,
      audio: parsed.audio ?? DEFAULTS.audio,
    }
  } catch {
    return DEFAULTS
  }
}

function save(userId: string | null, settings: AutoDownloadSettings): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(settings))
  } catch {
    // Private mode / quota — the setting just won't survive a reload.
  }
}

function unlockedStorageKey(userId: string | null): string {
  return `${STORAGE_PREFIX}_unlocked:${userId || 'anon'}`
}

/**
 * Attachments already shown to this user, on this device, across reloads.
 * Populated both by an explicit tap and by auto-download actually fetching a
 * file, so disabling auto-download later never re-hides something already
 * downloaded.
 */
function loadUnlocked(userId: string | null): Set<string> {
  try {
    const raw = localStorage.getItem(unlockedStorageKey(userId))
    if (!raw) return new Set<string>()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return new Set<string>()
    return new Set(parsed.slice(-UNLOCKED_CAP))
  } catch {
    return new Set<string>()
  }
}

function saveUnlocked(userId: string | null, unlocked: Set<string>): void {
  try {
    const urls = Array.from(unlocked)
    const capped = urls.length > UNLOCKED_CAP ? urls.slice(urls.length - UNLOCKED_CAP) : urls
    localStorage.setItem(unlockedStorageKey(userId), JSON.stringify(capped))
  } catch {
    // Private mode / quota: the setting just won't survive a reload.
  }
}

interface AttachmentSettingsState extends AutoDownloadSettings {
  /** Whose settings are currently loaded. */
  userId: string | null
  /** Point the store at a user; re-reads that user's stored values. */
  bindUser: (userId: string | null) => void
  set: (kind: AutoDownloadKind, value: boolean) => void
  /**
   * Attachments already shown to this user, whether by an explicit tap or by
   * auto-download fetching them on arrival. Keyed by attachment URL so a
   * message re-render (or a store refresh) doesn't make an already loaded
   * image disappear behind its placeholder again, and persisted per user so
   * a reload or a later toggle change doesn't either.
   */
  unlocked: Set<string>
  unlock: (url: string) => void
  isUnlocked: (url: string) => boolean
}

export const useAttachmentSettings = create<AttachmentSettingsState>((setState, getState) => ({
  ...load(null),
  userId: null,
  unlocked: loadUnlocked(null),

  bindUser: (userId) => {
    if (getState().userId === userId) return
    // Switch to the new user's own settings and their own unlocked set.
    setState({ userId, ...load(userId), unlocked: loadUnlocked(userId) })
  },

  set: (kind, value) => {
    setState((s) => {
      const next = { photos: s.photos, documents: s.documents, videos: s.videos, audio: s.audio, [kind]: value }
      save(s.userId, next as AutoDownloadSettings)
      return { [kind]: value } as Partial<AttachmentSettingsState>
    })
  },

  unlock: (url) => {
    if (!url) return
    setState((s) => {
      if (s.unlocked.has(url)) return {}
      const next = new Set(s.unlocked)
      next.add(url)
      if (next.size > UNLOCKED_CAP) {
        // Over the cap: drop the oldest entries, keep the most recent ones.
        const trimmed = new Set(Array.from(next).slice(next.size - UNLOCKED_CAP))
        saveUnlocked(s.userId, trimmed)
        return { unlocked: trimmed }
      }
      saveUnlocked(s.userId, next)
      return { unlocked: next }
    })
  },

  isUnlocked: (url) => getState().unlocked.has(url),
}))

/**
 * Which toggle governs a given attachment.
 *
 * Accepts both the SDK's `AttachmentKind` (`photo`/`voice`/…) and the UI
 * message type (`image`/`file`/…) so callers can pass whichever they hold.
 */
export function settingKeyFor(kind: string | undefined): AutoDownloadKind {
  switch (kind) {
    case 'photo':
    case 'image':
      return 'photos'
    case 'video':
      return 'videos'
    case 'audio':
    case 'voice':
      return 'audio'
    default:
      return 'documents'
  }
}

/**
 * Keep the store pointed at whoever is signed in.
 *
 * Subscribed at module scope so every consumer — settings screen and every
 * message bubble — sees the same identity without each one wiring it up.
 * `presenceId` is the chat identity; `user.id` covers the window before it
 * resolves.
 */
function currentUserId(): string | null {
  const s = useAuthStore.getState()
  return s.presenceId ?? s.user?.id ?? null
}

useAttachmentSettings.getState().bindUser(currentUserId())
useAuthStore.subscribe(() => {
  useAttachmentSettings.getState().bindUser(currentUserId())
})
